import React, { useState, useEffect } from 'react';
import {
    Grid,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Popover,
    Avatar,
    TableSortLabel
} from '@mui/material';
import { CustomAccordion, CustomModal, TableRowsSkeleton } from '../../../shared';
import { ActionsIcon, Edit, Trash } from '../../../../assets';
import { partnersLogo } from '../../../../assets/partners';
import EditDataSource from '../editDataSource';
import { useDispatch, useSelector } from 'react-redux';
import { deleteDataSources, getDataSourceById, getDataSourcesList } from '../../../../redux/thunks/dataSourcesThunk'
import { actions } from '../../../../redux/reducers/dataSourcesReducer';
import ConfirmationDelete from '../confirmationDelete';
import { getSortedData } from '../../../../utility';

function DataSourceTable(props) {
    const { } = props;
    const dispatch = useDispatch()
    const { company } = useSelector(state => state.company)
    const { dataSourcesList, isEditTrigger, selectAll, selected, isChanged, isLoading} =  useSelector(state => state.dataSources)
    const [anchorEl, setAnchorEl] = useState(null);
    const [dataSourceName, setDataSourceName] = useState("");
    const [openDelete, setOpenDelete] = useState(false);
    const [partnerOption, setPartnerOption] = useState(null);
    const [selectedDataSourceId, setSelectedDataSourceId] = useState(null);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };

    const sortedData = React.useMemo(() => {
        return getSortedData(orderBy, order, dataSourcesList);
    }, [order, orderBy, dataSourcesList]);    

    const handleShowModal = () => {
        dispatch(actions.handleEditOpen())
    };

    const handleCloseModal = () => {
        setDataSourceName('');
        setPartnerOption(null);
        dispatch(actions.handleEditClose())
    };

    const handleCheckboxChange = (id) => {
        dispatch(actions.handleSelected({ type: 'SET_SELECTED_ROWS', selectedId: id }));
    };

    const handleSelectAll = () => {
        dispatch(actions.handleSelected({ type: 'SELECT_ALL' }));
    };

    const handlePopoverOpen = (event, selectedDataSource) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
        setSelectedDataSourceId(selectedDataSource);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const partnersIconMapping = (name) => {
        const brand = name?.toLowerCase();
        return partnersLogo[brand] || '';
    };

    const handleEditDataSource = () => {
        dispatch(getDataSourceById({companyId: company.id, dataSourceId: selectedDataSourceId}))
        handlePopoverClose();
        handleShowModal();
    }

    const onclose = () =>{
        handlePopoverClose();
        setOpenDelete(!openDelete)
    }
    const handleDeleteOpen = () =>{
        setOpenDelete(true)
    }
    const handleDelete = () => {
        handlePopoverClose();
        if(selectedDataSourceId){
            dispatch(deleteDataSources({companyId: company.id, data: {ids: [selectedDataSourceId]}})).then(result =>{
                if(result.payload.status_code === 200){
                    dispatch(actions.setSelectAllFalse());
                    onclose();
                }
            });
        }
    }

    const handleTableRowClick = (event, id) => {
        const isCheckboxColumn = event.target.closest('.table-row-checkbox');
        const isActionsColumn = event.target.closest('.table-row-actions');
        const isPopoverOpen = Boolean(anchorEl);
        if (!isCheckboxColumn && !isActionsColumn && !isPopoverOpen) {
            dispatch(getDataSourceById({companyId: company.id, dataSourceId: id}))
            handlePopoverClose();
            handleShowModal();
        }
    };

    useEffect(()=>{
        if(company.id){
            dispatch(getDataSourcesList({companyId: company.id}))
        }

    },[company.id, dispatch,isChanged])

    const renderNoData = () => {
        return (
            <TableRow>
                <TableCell colSpan={5}>
                    <Box textAlign="center" p={3}>
                        <Typography className="text-sm-regular gray-900">
                            No data sources to display.
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const renderTable = () => {

        return dataSourcesList?.length > 0 ? sortedData?.map((row, index) => {
            return (
                <>
                    <TableRow key={row?.id} className='cursor-pointer'  onClick={(event) => handleTableRowClick(event, row.id)} data-testid="ds_table">
                        <TableCell className='table-row-data no-border table-row-checkbox'>
                            <Checkbox
                                checked={selected.includes(row?.id)}
                                onChange={() => handleCheckboxChange(row.id)}
                            />
                        </TableCell>
                        <TableCell className='table-row-data no-border' >
                            {row?.id}
                        </TableCell>
                        <TableCell className='table-row-data no-border font-weight-500 gray-900' >
                            {row?.name}
                        </TableCell>
                        <TableCell className='table-row-data no-border font-weight-500 gray-900'>
                            <Box className="flex-center-flex-start gap-12">
                                <Avatar
                                    src={partnersIconMapping(row.partner?.name)}
                                    className="data-source-avatar"
                                    alt={row?.partner?.name}
                                >
                                    {row?.partner?.name?.charAt(0)}
                                </Avatar>
                                {row?.partner?.name}
                            </Box>
                        </TableCell>
                        <TableCell className='table-row-data no-border cursor-pointer table-row-actions' >
                            <img src={ActionsIcon} height={20} width={20} onClick={(event) => handlePopoverOpen(event, row.id)} />
                            <Popover
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handlePopoverClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                disableScrollLock={true}
                                sx={{
                                    '& .MuiPaper-root': {
                                        boxShadow:
                                            '0px 4px 6px -2px rgba(16, 24, 40, 0.05), inset 0px 8px 10px -7px rgba(16, 24, 40, 0.10)',
                                    },
                                }}
                                data-testid="dataSource_popover"
                            >
                                <Box>
                                    <Box id="dataSource_popover" className="action-button-box table-row-actions"
                                        onClick={() => {
                                            handleEditDataSource(row);
                                        }}
                                    >
                                        <Edit color='#667085' />
                                        <Typography className='action-button-text' >
                                            Edit
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', flexDirection: 'row', gap: '12px' }}
                                        onClick={handleDeleteOpen}
                                        className="table-row-actions"
                                        data-testid="delete-option"
                                    >
                                        <Trash color='#667085' />
                                        <Typography className='action-button-text'>
                                            Delete
                                        </Typography>
                                    </Box>
                                </Box>
                            </Popover>
                        </TableCell>
                    </TableRow>
                </>)
        })
            : renderNoData();
    };

    const dataSourceTableComponent = () => (
        <TableContainer component={Paper} className='no-box-shadow no-border'>
                    <Table className='table-border-collapse no-border'>
                        <TableHead className="queries-table-heading">
                            <TableRow>
                                <TableCell className="queries-headers-title" data-testid="data_source_table_selectAll">
                                    <Checkbox
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        
                                    />
                                </TableCell>
                                <TableCell className="queries-headers-title">
                                    <TableSortLabel active={orderBy === "id"} 
                                        direction={orderBy === "id" ? order : 'asc'}
                                        onClick={createSortHandler("id")}>
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="queries-headers-title">
                                    <TableSortLabel active={orderBy === "name"}
                                        direction={orderBy === "name" ? order : 'asc'}
                                        onClick={createSortHandler("name")}>
                                        Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="queries-headers-title">
                                    <TableSortLabel active={orderBy === "partner.name"}
                                        direction={orderBy === "partner.name" ? order : 'asc'}
                                        onClick={createSortHandler("partner.name")}>
                                        Partner
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell className="queries-headers-title"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRowsSkeleton
                                    rows={10}
                                    columns={5}
                                />
                            ) : (
                                renderTable()
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
    )

    const dataSources = () => {
        return (<Grid container spacing={'24px'} className='d-flex flex-direction-row'>
            <Grid item xs={12}>
                <Box>
                    {dataSourceTableComponent()}
                </Box>
            </Grid>
        </Grid>);
    }
    return (
        <Box className ="b1px-border-box-br8px" data-testid="data-source-table">
            <CustomAccordion
                title="See Full List Below"
                component={dataSources()}
                accordionContainerBackground="bg-primary-50"
                accordionTitleColor="primary-color"
                acoordianDetailsBackground='bg-gray-50'
                byDefaultOpen={true}
            />
            <CustomModal 
                modalOpen={isEditTrigger} 
                component={
                    <EditDataSource
                        handleClose={handleCloseModal} 
                        dataSourceName={dataSourceName}
                        selectedPartner={partnerOption}
                    />
                } 
                onClose = {handleCloseModal}
            />
            <ConfirmationDelete 
                open={openDelete} 
                onClosed={onclose} 
                onConfirms={handleDelete} />
        </Box>
    )
};


export default DataSourceTable;